import { CircularProgress, Tooltip } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import { checkImageLibraryRequiredActions } from 'app/plugins/openstack/components/images/checkImageLibraryRequiredActions'
import ActionRequiredModalTrigger, {
  ActionRequiredIssue,
} from 'core/components/ActionRequiredModalTrigger'
import ExternalLink from 'core/components/ExternalLink'
import ListContainer from 'core/containers/ListContainer'
import Badge from 'core/elements/badge'
import Button from 'core/elements/button/Button'
import Text from 'core/elements/Text'
import { useLocalNetworkAccessAllowed } from 'core/hooks/useLocalNetworkAccessAllowed'
import Theme from 'core/themes/model'
import DataKeys from 'k8s/DataKeys'
import React, { useMemo, useState } from 'react'
import { useControlPlaneServicesCheck } from './ControlPlaneServicesContext'
import { AcceptCertificate } from './HealthStatusBadge'
import { getHealthStatus } from './helper'
import { ServiceStatus } from './selector'
import TroubleShootModal from './TroubleShootModal'
import UpdateImageAdminEndpoint from './UpdateImageAdminEndpoint'
import { ServiceNameMap } from './useControlPlaneServicesHealth'

const useColumnStyles = makeStyles((theme: Theme) => ({
  serviceHealthBadge: {
    cursor: 'pointer',
  },
  refreshButton: {
    padding: 4,
    marginLeft: 8,
    '&:hover': {
      backgroundColor: theme.components.table.hoverBackground,
    },
  },

  actionCell: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
}))

interface HealthStatusBadgeProps {
  value: any
  serviceType: string
  endpoint?: string
}

export const ErrorMessageComponent = ({ value, item }: { value: string; item: any }) => {
  return (
    <Text variant="body2">
      {item?.status === ServiceStatus.CertificateError ? (
        <AcceptCertificate endpoint={item?.url} />
      ) : (
        value
      )}
    </Text>
  )
}

export const HealthStatusBadge: React.FC<HealthStatusBadgeProps> = ({
  value,
  serviceType,
  endpoint,
}) => {
  const classes = useColumnStyles()
  const canonicalStatusKey =
    value === ServiceStatus.NetworkError
      ? 'NetworkError'
      : value === ServiceStatus.CertificateError
      ? 'CertificateError'
      : value
  const { variant } = getHealthStatus(canonicalStatusKey)
  const label = value
  const serviceName = ServiceNameMap[serviceType] || serviceType
  const [troubleshootOptions, setTroubleshootOptions] = useState<boolean>(false)

  return (
    <>
      <Badge
        className={variant === 'warning' ? classes.serviceHealthBadge : ''}
        variant={variant}
        text={label}
        onClick={(e) => {
          if (value === ServiceStatus.NetworkError) {
            setTroubleshootOptions(true)
            e.stopPropagation()
            e.preventDefault()
          }
        }}
      />
      {value === ServiceStatus.NetworkError && (
        <TroubleShootModal
          open={!!troubleshootOptions}
          onClose={() => setTroubleshootOptions(false)}
          serviceName={serviceName}
          linkUrl={''}
          title="Troubleshooting Steps"
          maxWidth={800}
          steps={[
            {
              id: 1,
              text: `Make sure that the ${serviceName} service is accessible from the network you are on.`,
            },
            {
              id: 2,
              text: <AcceptCertificate endpoint={endpoint} />,
            },
          ]}
        />
      )}
    </>
  )
}

const columns = ({
  setOpenUpdateImageAdminEndpoint,
  checkingServices,
  reloadService,
  classes,
  isBrowserLocalNetworkAccessAllowed,
}: {
  setOpenUpdateImageAdminEndpoint: (open: boolean) => void
  checkingServices: Set<string>
  reloadService: (serviceType: string) => Promise<void>
  classes: ReturnType<typeof useColumnStyles>
  isBrowserLocalNetworkAccessAllowed: boolean
}) => [
  {
    key: 'displayName',
    label: 'Services',
    CellComponent: ({ value, item }) => <Text variant="caption1">{value || item?.type}</Text>,
  },
  {
    key: 'url',
    label: 'URL',
    display: false,
    CellComponent: ({ value }) =>
      value?.split(',').map((url, idx) => (
        <React.Fragment key={`url-${idx}`}>
          <ExternalLink url={url.trim()}>{url.trim()}</ExternalLink>
          <br />
        </React.Fragment>
      )),
  },
  {
    key: 'status',
    label: 'Status',
    CellComponent: ({ value, item }) => {
      const isChecking = checkingServices.has(item?.type)
      const issues: ActionRequiredIssue[] = useMemo(() => {
        if (item?.name !== 'glance-admin' && item?.name !== 'image (admin)') {
          return []
        }

        const needToValidateCert = value === ServiceStatus.CertificateError
        const glanceHostNotReachable = value === ServiceStatus.NetworkError

        return checkImageLibraryRequiredActions({
          needToValidateCert,
          glanceHostNotReachable,
          isBrowserLocalNetworkAccessAllowed,
          hostEndpoint: item?.url,
        })
      }, [isBrowserLocalNetworkAccessAllowed, item?.url, value])

      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isChecking ? (
            <Text variant="body2">
              <CircularProgress size={16} /> Loading
            </Text>
          ) : issues.length > 0 ? (
            <ActionRequiredModalTrigger
              issues={issues}
              modalTitle="Needs Action"
              modalIntroText="The following actions are required to access the Image Library Endpoint:"
              trigger={({ openWithEvent }) => (
                <Badge
                  variant="warning"
                  text={`Needs Action (${issues.length})`}
                  ellipsisAt={50}
                  onClick={openWithEvent}
                />
              )}
            />
          ) : (
            <HealthStatusBadge value={value} serviceType={item?.type} endpoint={item?.url} />
          )}
        </div>
      )
    },
  },
  {
    key: 'error',
    label: 'Error Details',
    CellComponent: ({ value, item }) => <ErrorMessageComponent value={value} item={item} />,
  },

  {
    key: 'type',
    label: 'Actions',
    CellComponent: ({ value, item }) => {
      const isChecking = checkingServices.has(item?.type)

      return (
        <div className={classes.actionCell}>
          <Tooltip title="Refresh this service">
            <Button
              variant="secondary"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                reloadService(item?.type)
              }}
              size="small"
              icon={'refresh'}
              disabled={isChecking}
            >
              Refresh
            </Button>
          </Tooltip>
          {value === 'image (admin)' && (
            <Button
              variant="secondary"
              size="small"
              icon={'edit'}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                setOpenUpdateImageAdminEndpoint(true)
              }}
            >
              Update Endpoint
            </Button>
          )}
        </div>
      )
    },
  },
]

/**
 * @summary API Services Page is responsible for displaying the health of internal API services.
 * @returns List of internal API services with their health status and necessary actions.
 */
export default function ApiServicesPage() {
  const classes = useColumnStyles()
  const [openUpdateImageAdminEndpoint, setOpenUpdateImageAdminEndpoint] = useState(false)
  const isBrowserLocalNetworkAccessAllowed = useLocalNetworkAccessAllowed()
  const {
    servicesList,
    isLoading,
    checkingServices,
    reload,
    reloadService,
    imageAdminEndpoints,
  } = useControlPlaneServicesCheck()

  return (
    <>
      {openUpdateImageAdminEndpoint && (
        <UpdateImageAdminEndpoint
          onClose={() => setOpenUpdateImageAdminEndpoint(false)}
          servicesList={servicesList}
          imageAdminEndpoints={imageAdminEndpoints}
          reload={reload}
        />
      )}
      <ListContainer<any, any>
        dataKey={DataKeys.ControlPlaneServicesHealth}
        uniqueIdentifier={'name'}
        label="API Endpoints Health"
        searchTargets={['name', 'clusterName']}
        loading={isLoading}
        loadingMessage={'Checking API endpoints health...'}
        onRefresh={() => reload(true, true)}
        data={servicesList}
        columns={columns({
          setOpenUpdateImageAdminEndpoint,
          checkingServices,
          reloadService,
          classes,
          isBrowserLocalNetworkAccessAllowed,
        })}
        emptyContent="No services found"
        hideCheckboxes
        showBreadcrumbs={false}
      />
    </>
  )
}
